import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    LogIn,
    LogOut,
    Trash2,
    Download,
    Mail,
    KeyRound,
    Eye,
    EyeOff,
    AlertTriangle,
    CheckCircle2,
    Loader2,
} from "lucide-react";
import { Header } from "@/sections/Header";
import { Footer } from "@/sections/Footer";
import { useLang } from "@/i18n/LanguageContext";
import { authApi } from "@/lib/appApi";

const TOKEN_KEY = "606fitness_app_token";

const Input = ({ icon: Icon, ...props }) => (
    <div className="relative">
        {Icon && (
            <Icon className="absolute top-1/2 -translate-y-1/2 start-3 w-4 h-4 text-neutral-500 pointer-events-none" />
        )}
        <input
            {...props}
            className={`w-full bg-black/40 border border-white/10 focus:border-[var(--primary)] focus:outline-none rounded-full ${
                Icon ? "ps-10" : "ps-4"
            } pe-4 py-3 text-sm sm:text-base text-white placeholder:text-neutral-500 transition-colors`}
        />
    </div>
);

const Alert = ({ kind, children, testId }) => {
    const styles = {
        error:
            "bg-red-500/10 border-red-500/40 text-red-300",
        success:
            "bg-green-500/10 border-green-500/40 text-green-300",
        warn:
            "bg-[var(--primary)]/10 border-[var(--primary)]/40 text-[var(--primary)]",
    };
    return (
        <div
            data-testid={testId}
            className={`rounded-2xl border p-4 text-sm leading-relaxed ${styles[kind]}`}
        >
            {children}
        </div>
    );
};

export default function AccountPage() {
    const { t } = useLang();
    const ax = t.account;

    const [token, setToken] = useState(() => {
        try {
            return localStorage.getItem(TOKEN_KEY) || "";
        } catch {
            return "";
        }
    });
    const [user, setUser] = useState(null);
    const [mode, setMode] = useState("login"); // login | forgot | reset
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    // Form state
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [code, setCode] = useState("");
    const [newPassword, setNewPassword] = useState("");

    // Delete confirmation
    const [confirmInput, setConfirmInput] = useState("");
    const [reason, setReason] = useState("");
    const [confirming, setConfirming] = useState(false);
    const [deleted, setDeleted] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // Load user when we have a token
    useEffect(() => {
        let cancelled = false;
        const loadUser = async () => {
            if (!token) return;
            setBusy(true);
            try {
                const me = await authApi.me(token);
                if (!cancelled) setUser(me);
            } catch (e) {
                if (cancelled) return;
                // token invalid → log out silently
                localStorage.removeItem(TOKEN_KEY);
                setToken("");
                setUser(null);
            } finally {
                if (!cancelled) setBusy(false);
            }
        };
        loadUser();
        return () => {
            cancelled = true;
        };
    }, [token]);

    const errorText = useMemo(() => {
        if (!error) return "";
        if (error === "network") return ax.errors.network;
        if (error === "invalidCredentials") return ax.errors.invalidCredentials;
        if (error === "confirmRequired") return ax.errors.confirmRequired;
        if (error === "emailRequired") return ax.errors.emailRequired;
        if (error === "passwordRequired") return ax.errors.passwordRequired;
        return ax.errors.generic;
    }, [error, ax]);

    const resetAlerts = () => {
        setError("");
        setSuccess("");
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        resetAlerts();
        if (!email) return setError("emailRequired");
        if (!password) return setError("passwordRequired");
        setBusy(true);
        try {
            const data = await authApi.login(email, password);
            const tk = data.token || data.access_token;
            localStorage.setItem(TOKEN_KEY, tk);
            setToken(tk);
            setUser(data.user || null);
            setPassword("");
        } catch (err) {
            setError(err.code || err.message || "generic");
        } finally {
            setBusy(false);
        }
    };

    const handleForgot = async (e) => {
        e.preventDefault();
        resetAlerts();
        if (!email) return setError("emailRequired");
        setBusy(true);
        try {
            await authApi.forgotPassword(email);
            setSuccess(ax.codeSent);
            setMode("reset");
        } catch (err) {
            setError(err.code || err.message || "generic");
        } finally {
            setBusy(false);
        }
    };

    const handleReset = async (e) => {
        e.preventDefault();
        resetAlerts();
        setBusy(true);
        try {
            await authApi.resetPassword(email, code, newPassword);
            setMode("login");
            setSuccess("");
            setCode("");
            setNewPassword("");
            setPassword("");
        } catch (err) {
            setError(err.code || err.message || "generic");
        } finally {
            setBusy(false);
        }
    };

    const handleLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUser(null);
        setEmail("");
        setPassword("");
        resetAlerts();
    };

    const handleDownload = () => {
        if (!user) return;
        const blob = new Blob([JSON.stringify(user, null, 2)], {
            type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `606fitness-account-${
            user.id || user._id || user.email || "data"
        }.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        setSuccess(ax.successDownloaded);
    };

    const handleDelete = async () => {
        resetAlerts();
        if (confirmInput !== "DELETE") {
            setError("confirmRequired");
            return;
        }
        setBusy(true);
        try {
            await authApi.deleteAccount(token, reason);
            localStorage.removeItem(TOKEN_KEY);
            setDeleted(true);
            setUser(null);
            setToken("");
        } catch (err) {
            setError(err.code || err.message || "generic");
        } finally {
            setBusy(false);
            setConfirming(false);
        }
    };

    const renderLoggedOut = () => (
        <div className="space-y-6">
            {mode === "login" && (
                <form
                    onSubmit={handleLogin}
                    className="space-y-4 card-dark rounded-2xl p-6 sm:p-8"
                    data-testid="account-login-form"
                >
                    <h2 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
                        <LogIn className="w-6 h-6 text-[var(--primary)]" />
                        {ax.loginH}
                    </h2>
                    {errorText && (
                        <Alert kind="error" testId="account-error">
                            {errorText}
                        </Alert>
                    )}
                    <div>
                        <label className="block text-xs mb-1.5 text-neutral-400">
                            {ax.email}
                        </label>
                        <Input
                            icon={Mail}
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            data-testid="account-email-input"
                        />
                    </div>
                    <div>
                        <label className="block text-xs mb-1.5 text-neutral-400">
                            {ax.password}
                        </label>
                        <div className="relative">
                            <Input
                                icon={KeyRound}
                                type={showPw ? "text" : "password"}
                                autoComplete="current-password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                data-testid="account-password-input"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw((v) => !v)}
                                className="absolute top-1/2 -translate-y-1/2 end-3 text-neutral-500 hover:text-white"
                                aria-label="toggle password visibility"
                            >
                                {showPw ? (
                                    <EyeOff className="w-4 h-4" />
                                ) : (
                                    <Eye className="w-4 h-4" />
                                )}
                            </button>
                        </div>
                    </div>
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={() => {
                                resetAlerts();
                                setMode("forgot");
                            }}
                            data-testid="account-forgot-link"
                            className="text-xs text-neutral-400 hover:text-[var(--primary)] transition-colors"
                        >
                            {ax.forgot}
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="account-login-submit"
                            className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold disabled:opacity-50"
                        >
                            {busy ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                                <LogIn className="w-4 h-4" />
                            )}
                            {ax.loginBtn}
                        </button>
                    </div>
                </form>
            )}

            {mode === "forgot" && (
                <form
                    onSubmit={handleForgot}
                    className="space-y-4 card-dark rounded-2xl p-6 sm:p-8"
                    data-testid="account-forgot-form"
                >
                    <h2 className="text-2xl sm:text-3xl font-bold">{ax.forgotH}</h2>
                    <p className="text-sm text-neutral-400">{ax.forgotIntro}</p>
                    {errorText && <Alert kind="error">{errorText}</Alert>}
                    <Input
                        icon={Mail}
                        type="email"
                        placeholder={ax.email}
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        data-testid="account-forgot-email"
                    />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={() => {
                                resetAlerts();
                                setMode("login");
                            }}
                            className="text-xs text-neutral-400 hover:text-[var(--primary)]"
                        >
                            {ax.backToLogin}
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="account-forgot-submit"
                            className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {ax.sendCode}
                        </button>
                    </div>
                </form>
            )}

            {mode === "reset" && (
                <form
                    onSubmit={handleReset}
                    className="space-y-4 card-dark rounded-2xl p-6 sm:p-8"
                    data-testid="account-reset-form"
                >
                    <h2 className="text-2xl sm:text-3xl font-bold">{ax.forgotH}</h2>
                    {success && <Alert kind="success">{success}</Alert>}
                    {errorText && <Alert kind="error">{errorText}</Alert>}
                    <Input
                        type="text"
                        inputMode="numeric"
                        placeholder={ax.code}
                        value={code}
                        onChange={(e) => setCode(e.target.value)}
                        data-testid="account-reset-code"
                    />
                    <Input
                        type="password"
                        placeholder={ax.newPassword}
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        data-testid="account-reset-new-password"
                    />
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <button
                            type="button"
                            onClick={() => {
                                resetAlerts();
                                setMode("login");
                            }}
                            className="text-xs text-neutral-400 hover:text-[var(--primary)]"
                        >
                            {ax.backToLogin}
                        </button>
                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="account-reset-submit"
                            className="btn-primary inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-bold disabled:opacity-50"
                        >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            {ax.resetBtn}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );

    const renderLoggedIn = () => (
        <div className="space-y-6">
            {/* Identity bar */}
            <div className="flex items-center justify-between gap-3 flex-wrap card-dark rounded-2xl p-4 sm:p-5">
                <div className="text-sm">
                    <span className="text-neutral-500">{ax.loggedInAs} </span>
                    <span className="text-white font-bold" data-testid="account-current-email">
                        {user?.email || email}
                    </span>
                </div>
                <button
                    onClick={handleLogout}
                    data-testid="account-logout"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 text-xs hover:border-[var(--primary)] hover:text-[var(--primary)] transition-all"
                >
                    <LogOut className="w-4 h-4" /> {ax.logout}
                </button>
            </div>

            {/* My data */}
            <div className="card-dark rounded-2xl p-6 sm:p-8 space-y-4">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h2 className="text-xl sm:text-2xl font-bold">{ax.myData}</h2>
                        <p className="text-sm text-neutral-400 mt-1 max-w-md">
                            {ax.dataDescription}
                        </p>
                    </div>
                    <button
                        onClick={handleDownload}
                        data-testid="account-download-data"
                        disabled={!user || busy}
                        className="btn-primary inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold disabled:opacity-50"
                    >
                        <Download className="w-4 h-4" /> {ax.downloadData}
                    </button>
                </div>

                {success && <Alert kind="success">{success}</Alert>}

                <pre
                    data-testid="account-data-json"
                    className="text-[11px] sm:text-xs bg-black/60 border border-white/5 rounded-xl p-4 overflow-auto max-h-72"
                    dir="ltr"
                >
                    {user ? JSON.stringify(user, null, 2) : "Loading..."}
                </pre>
            </div>

            {/* Danger zone */}
            <div
                data-testid="account-danger-zone"
                className="rounded-2xl border border-red-500/30 bg-red-500/[0.04] p-6 sm:p-8 space-y-4"
            >
                <div className="flex items-center gap-2 text-red-400 font-display tracking-widest text-xs">
                    <AlertTriangle className="w-4 h-4" />
                    {ax.dangerZone}
                </div>
                <h2 className="text-xl sm:text-2xl font-bold">{ax.deleteH}</h2>
                <Alert kind="error">{ax.deleteWarning}</Alert>

                {!confirming ? (
                    <button
                        onClick={() => {
                            resetAlerts();
                            setConfirming(true);
                        }}
                        data-testid="account-delete-start"
                        className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold bg-red-500/15 border border-red-500/50 text-red-300 hover:bg-red-500 hover:text-white transition-all"
                    >
                        <Trash2 className="w-4 h-4" /> {ax.deleteBtn}
                    </button>
                ) : (
                    <div className="space-y-3">
                        <p className="text-sm text-neutral-300">{ax.confirmInstruction}</p>
                        {errorText && <Alert kind="error">{errorText}</Alert>}
                        <Input
                            type="text"
                            placeholder={ax.confirmPlaceholder}
                            value={confirmInput}
                            onChange={(e) => setConfirmInput(e.target.value)}
                            data-testid="account-delete-confirm-input"
                        />
                        <Input
                            type="text"
                            placeholder={ax.reasonOptional}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            data-testid="account-delete-reason"
                        />
                        <div className="flex items-center justify-end gap-3 flex-wrap">
                            <button
                                onClick={() => {
                                    setConfirming(false);
                                    setConfirmInput("");
                                    setReason("");
                                    resetAlerts();
                                }}
                                className="px-4 py-2.5 rounded-full text-sm border border-white/15 text-neutral-300 hover:border-white/30"
                            >
                                {ax.cancel}
                            </button>
                            <button
                                onClick={handleDelete}
                                disabled={busy || confirmInput !== "DELETE"}
                                data-testid="account-delete-confirm"
                                className="inline-flex items-center gap-2 px-5 py-3 rounded-full text-sm font-bold bg-red-500 text-white hover:bg-red-600 disabled:opacity-40"
                            >
                                {busy ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Trash2 className="w-4 h-4" />
                                )}
                                {ax.deleteBtn}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div data-testid="account-page" className="min-h-screen bg-[var(--bg)] text-white">
            <Header />

            <main className="max-w-3xl mx-auto px-5 sm:px-8 pt-32 sm:pt-40 pb-20">
                <div className="mb-8 pb-6 border-b border-white/10">
                    <span className="font-display text-[var(--primary)] text-sm tracking-[0.3em]">
                        {ax.pageSubtitle}
                    </span>
                    <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-black leading-tight">
                        {ax.pageTitle}
                    </h1>
                    <p className="mt-4 text-sm sm:text-base text-neutral-400 max-w-2xl leading-relaxed">
                        {ax.intro}
                    </p>
                </div>

                {deleted ? (
                    <div className="space-y-4">
                        <Alert kind="success" testId="account-deleted-success">
                            <div className="flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5" />
                                <span>{ax.successDeleted}</span>
                            </div>
                        </Alert>
                        <Link
                            to="/"
                            className="inline-flex items-center gap-2 btn-primary px-5 py-3 rounded-full text-sm font-bold"
                        >
                            {t.header.cta}
                        </Link>
                    </div>
                ) : token && user ? (
                    renderLoggedIn()
                ) : (
                    renderLoggedOut()
                )}
            </main>

            <Footer />
        </div>
    );
}
